import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { formatDate } from "@/lib/dates";
import { Clock, CheckCircle2, ChevronDown, ChevronRight, FileDown } from "lucide-react";

const money = (v) => `$${(Number(v) || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_TONE = {
  draft: "bg-slate-100 text-slate-600",
  awaiting_review: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  payroll_ready: "bg-indigo-100 text-indigo-700",
  summary_sent: "bg-teal-100 text-teal-700",
  exported: "bg-blue-100 text-blue-700",
  paid: "bg-green-100 text-green-700",
};
const STATUS_LABEL = {
  draft: "Draft", awaiting_review: "Awaiting Review", approved: "Approved",
  payroll_ready: "Ready for Payroll", summary_sent: "Summary Sent", exported: "Exported", paid: "Paid",
};

function downloadSummary(id, weekEnd) {
  const token = localStorage.getItem("ts_token");
  fetch(`${API}/timesheets/${id}/summary-pdf`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.blob())
    .then((b) => {
      const url = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = url; a.download = `Payment-Summary-${weekEnd}.pdf`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    });
}

function WeekCard({ ts, isContractor }) {
  const [open, setOpen] = useState(false);
  const t = ts.totals || {};
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid={`ts-week-${ts.week_start}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors">
        <div className="flex items-center gap-3 text-left">
          {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          <div>
            <p className="font-semibold text-navy">Week {formatDate(ts.week_start)} – {formatDate(ts.week_end)}</p>
            <p className="text-xs text-slate-500 mt-0.5">{t.total_days_worked || 0} days · {t.total_jobs || 0} jobs · {t.total_hours || 0}h{t.metres ? ` · ${t.metres}m` : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-lg font-heading font-bold text-navy">{money(t.weekly_total)}</span>
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_TONE[ts.status] || STATUS_TONE.draft}`}>{STATUS_LABEL[ts.status] || ts.status}</span>
        </div>
      </button>
      {open && (
        <div className="border-t border-slate-100">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                  <th className="text-left px-4 py-2 font-semibold">Date</th>
                  <th className="text-left px-3 py-2 font-semibold">Job</th>
                  <th className="text-left px-3 py-2 font-semibold">Site</th>
                  <th className="text-right px-2 py-2 font-semibold">Std</th>
                  <th className="text-right px-2 py-2 font-semibold">Sat</th>
                  <th className="text-right px-2 py-2 font-semibold">OT</th>
                  <th className="text-right px-2 py-2 font-semibold">Travel</th>
                  <th className="text-right px-2 py-2 font-semibold">Metres</th>
                  <th className="text-left px-2 py-2 font-semibold">Rate</th>
                  <th className="text-right px-4 py-2 font-semibold">Daily Total</th>
                  <th className="text-center px-2 py-2 font-semibold">✓</th>
                </tr>
              </thead>
              <tbody>
                {ts.lines.map((l) => (
                  <tr key={l.line_id} className="border-t border-slate-100">
                    <td className="px-4 py-2"><span className="font-medium text-navy">{l.day.slice(0, 3)}</span> {formatDate(l.date)}</td>
                    <td className="px-3 py-2 text-slate-600">{l.job_number}</td>
                    <td className="px-3 py-2 text-slate-500 max-w-[180px] truncate">{l.site_address || "—"}</td>
                    <td className="text-right px-2 py-2">{l.standard_hours || "—"}</td>
                    <td className="text-right px-2 py-2">{l.saturday_hours || "—"}</td>
                    <td className="text-right px-2 py-2">{l.overtime_hours || "—"}</td>
                    <td className="text-right px-2 py-2">{l.travel_hours || "—"}</td>
                    <td className="text-right px-2 py-2">{l.metres || "—"}</td>
                    <td className="px-2 py-2 text-xs text-slate-500 capitalize">{l.payment_method}</td>
                    <td className="text-right px-4 py-2 font-semibold text-navy">{money(l.daily_total)}</td>
                    <td className="text-center px-2 py-2">{l.approved ? <CheckCircle2 className="w-4 h-4 text-emerald-500 inline" /> : <span className="text-slate-300">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Weekly summary */}
          <div className="bg-slate-50/70 px-5 py-4 border-t border-slate-100">
            <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Weekly Summary</p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 text-sm">
              <Sum label="Standard Hours" value={t.standard_hours || 0} />
              <Sum label="Saturday Hours" value={t.saturday_hours || 0} />
              <Sum label="Overtime Hours" value={t.overtime_hours || 0} />
              <Sum label="Travel Hours" value={t.travel_hours || 0} />
              {!isContractor && <Sum label="Paid Leave Hours" value={t.paid_leave_hours || 0} />}
              <Sum label="Metres Completed" value={t.metres || 0} />
              <Sum label="Adjustments" value={money(t.adjustments)} />
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-3">
                <p className="text-xs text-emerald-600 font-semibold">{isContractor ? "Weekly Payment" : "Estimated Gross Pay"}</p>
                <p className="text-lg font-heading font-bold text-emerald-700 mt-0.5" data-testid={`ts-weekly-total-${ts.week_start}`}>{money(t.weekly_total)}</p>
              </div>
            </div>
            {ts.status !== "approved" && ts.status !== "paid" && ts.status !== "exported" && ts.status !== "summary_sent" && ts.status !== "payroll_ready" && (
              <p className="text-xs text-slate-400 mt-3 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Awaiting Administrator review — amounts may change until approved.</p>
            )}
            {isContractor && ["approved", "summary_sent", "paid"].includes(ts.status) && (
              <button onClick={() => downloadSummary(ts.id, ts.week_end)} data-testid={`ts-download-summary-${ts.week_start}`}
                className="mt-3 flex items-center gap-1.5 px-4 py-2 rounded-xl bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700">
                <FileDown className="w-4 h-4" />Download Payment Summary
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Sum({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-white p-3">
      <p className="text-xs text-slate-400 font-semibold">{label}</p>
      <p className="text-base font-heading font-bold text-navy mt-0.5">{value}</p>
    </div>
  );
}

export default function TimesheetOwnView() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/timesheets/me").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-slate-400">Loading timesheets…</div>;
  const isContractor = data.worker_type === "contractor";
  return (
    <div className="space-y-5" data-testid="timesheet-own-view">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-navy">{isContractor ? "Contractor Weekly Payment Summary" : "Employee Timesheet"}</h1>
        <p className="text-slate-500 mt-1 text-sm">Automatically compiled from your completed jobs · Monday–Sunday</p>
      </div>
      {data.timesheets.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center text-slate-400">
          No timesheets yet. Your completed &amp; approved jobs will appear here each week.
        </div>
      ) : (
        <div className="space-y-3">
          {data.timesheets.map((ts) => <WeekCard key={ts.id} ts={ts} isContractor={isContractor} />)}
        </div>
      )}
    </div>
  );
}
